import { action, flow, makeObservable, observable, runInAction } from "mobx";
import { apiFetcher } from "../utils/fetch";

export default class Rbac {
  constructor() {
    this.permissions = [];
    this.roles = [];
    this.isLoading = false;
    this.lastFetched = null;
    this.error = null;
    this.menu = null;

    makeObservable(this, {
      permissions: observable,
      roles: observable,
      isLoading: observable,
      lastFetched: observable,
      error: observable,
      setMenu: action,
      getRoles: flow,
      getPermissions: flow,
      fetchAllUserPermissions: flow,
      clearPermissions: action,
      setPermissions: action,
      menu: observable,
    });
  }

  // get roles with cache
  *getRoles() {
    if (Array.isArray(this.roles) && this.roles.length > 0) {
      return this.roles;
    }
    try {
      const response = yield apiFetcher().get(`/roles`);
      if (response.status === 200) {
        runInAction(() => {
          this.roles = response.data.data || [];
          this.error = null;
        });
        return this.roles;
      }
      throw new Error(response.error || "Failed to fetch roles");
    } catch (error) {
      runInAction(() => {
        this.error =
          error?.response?.data?.message ||
          error.message ||
          "Failed to fetch roles";
      });
      return null;
    }
  }

  // Optimized getPermissions with caching and deduplication
  *getPermissions(forceRefresh = false) {
    // Return cached permissions if they exist and not forcing refresh
    if (
      !forceRefresh &&
      Array.isArray(this.permissions) &&
      this.permissions.length > 0 &&
      this.lastFetched
    ) {
      const cacheAge = Date.now() - this.lastFetched;
      // Cache for 5 minutes (300,000ms)
      if (cacheAge < 300000) {
        return this.permissions;
      }
    }

    // Avoid duplicate requests if already loading
    if (this.isLoading) {
      // Wait for current request to complete
      while (this.isLoading) {
        yield new Promise((resolve) => setTimeout(resolve, 50));
      }
      return this.permissions;
    }

    try {
      const response = yield this.fetchAllUserPermissions();
      if (response.status === 200) {
        return this.permissions;
      }
      throw new Error(response.error || "Failed to fetch permissions");
    } catch (error) {
      throw error;
    }
  }

  *fetchAllUserPermissions() {
    try {
      runInAction(() => {
        this.isLoading = true;
        this.error = null;
      });

      const response = yield apiFetcher().get(`/user-permissions`);
      const permissions = response.data.data || [];

      // Normalize permissions data - extract permission strings from objects
      const normalizedPermissions = permissions
        .map((perm) => {
          if (typeof perm === "string") {
            return perm;
          } else if (perm && typeof perm === "object" && perm.permission) {
            return perm.permission;
          } else if (perm && typeof perm === "object" && perm._target) {
            // Handle MobX Proxy objects
            return perm._target.permission || perm._target;
          }
          // Fallback: convert to string
          return String(perm);
        })
        .filter(Boolean); // Remove any null/undefined values

      runInAction(() => {
        this.permissions = normalizedPermissions;
        this.lastFetched = Date.now();
        this.isLoading = false;
        this.error = null;
      });

      return { status: 200, data: this.permissions };
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
        this.error =
          error?.response?.data?.message ||
          error.message ||
          "Failed to fetch permissions";
      });

      return {
        status: error?.response?.status || 500,
        error: this.error,
      };
    }
  }

  // Action to manually set permissions (useful for hydration)
  setPermissions(permissions) {
    this.permissions = Array.isArray(permissions) ? permissions : [];
    this.lastFetched = Date.now();
    this.error = null;
  }

  // Action to clear permissions (useful for logout)
  clearPermissions() {
    this.permissions = [];
    this.lastFetched = null;
    this.error = null;
    this.isLoading = false;
  }

  // Synchronous permission checkers for better performance
  hasPermission(permission) {
    if (!permission || typeof permission !== "string") return false;
    if (this.permissions.length === 0) return false;
    
    return this.permissions.includes(permission);
  }

  hasAnyPermission(permissions) {
    if (!Array.isArray(permissions)) return false;
    return permissions.some((permission) => this.hasPermission(permission));
  }

  hasAllPermissions(permissions) {
    if (!Array.isArray(permissions)) return false;
    return permissions.every((permission) => this.hasPermission(permission));
  }

  // set sidebar menu
  setMenu(menu) {
    // Only update if menu is actually different (shallow compare)
    if (
      !Array.isArray(this.menu) ||
      this.menu.length !== menu.length ||
      this.menu.some((item, idx) => item.key !== menu[idx].key)
    ) {
      this.menu = menu;
    }
  }

  // check first menu that user has access to
  getFirstAccessibleMenu() {
    if (!Array.isArray(this.menu) || this.menu.length === 0) return null;
    return this.menu.find(
      (item) => !item.permission || this.hasPermission(item.permission)
    );
  }

  // Check if permissions are cached and fresh
  get isCached() {
    if (
      !this.lastFetched ||
      !Array.isArray(this.permissions) ||
      this.permissions.length === 0
    )
      return false;
    const cacheAge = Date.now() - this.lastFetched;
    return cacheAge < 300000; // 5 minutes
  }

  // Get cache age in seconds
  get cacheAge() {
    if (!this.lastFetched) return null;
    return Math.floor((Date.now() - this.lastFetched) / 1000);
  }
}
